import { Button, Position } from '@consigli/facade';
import { useDebouncedSearch, useMyUser } from '@consigli/hooks';
import clsx from 'clsx';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineArrowLeft, HiOutlineMagnifyingGlass, HiOutlineUser } from 'react-icons/hi2';
import { useNavigate } from 'react-router';

import { CenteredLayout } from '@/layouts/centered-content';
import { FluidLayout } from '@/layouts/fluid-content';
import { Route } from '@/routes';
import { isAecomUser } from '@/util/aecom';

import { ProjectManagementPage } from './project-management/project-management-page';
import { UserManagementInviteList } from './user-management/user-management-invites-list';
import { UserManagementPage } from './user-management/user-management-page';

export enum ManagementTab {
  USERS = 'USERS',
  INVITES = 'INVITES',
  PROJECTS = 'PROJECTS',
}

export const UserAndProjectManagementPage: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useMyUser();
  const canInviteUsers = isAecomUser(user?.email);
  const [selectedTab, setSelectedTab] = useState(ManagementTab.USERS);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedSearch(searchTerm, 500);

  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleTabClick = useCallback(
    (tab: ManagementTab) => {
      setSelectedTab(tab);
    },
    [setSelectedTab],
  );

  const tabLabels = {
    [ManagementTab.USERS]: t('user-and-project-management.users'),
    [ManagementTab.INVITES]: t('user-and-project-management.invites'),
    [ManagementTab.PROJECTS]: t('user-and-project-management.projects'),
  };

  return (
    <FluidLayout>
      <CenteredLayout>
        <nav className="flex justify-between w-full py-4 border-b border-day-secondary">
          <Button
            tertiary
            rounded
            className="mt-4 md:mt-0 text-base mx-7"
            icon={HiOutlineArrowLeft}
            onClick={handleBackClick}
          >
            {t('findings.back')}
          </Button>
        </nav>
        <div className="my-6 mx-10">
          <div className="relative flex items-center justify-center">
            <h1 className="absolute left-0 text-2xl font-bold">
              {t('user-and-project-management.title')}
            </h1>
            <div className="flex">
              {Object.values(ManagementTab).map((tab, index) => (
                <div
                  key={tab}
                  className={clsx(
                    'flex items-center px-4 py-2 rounded-lg transition-all duration-200 ease-in-out cursor-pointer',
                    index !== Object.values(ManagementTab).length - 1 && 'mr-3',
                    selectedTab === tab
                      ? 'bg-day-light-3 text-gray-900'
                      : 'text-gray-600 hover:bg-day-light-3 hover:text-gray-900',
                  )}
                  onClick={() => handleTabClick(tab)}
                  aria-hidden="true"
                >
                  <span className="font-medium whitespace-nowrap text-sm 2xl:text-base">
                    {tabLabels[tab]}
                  </span>
                </div>
              ))}
            </div>
            {selectedTab === ManagementTab.USERS ? (
              <div className="absolute right-0 flex items-center gap-3">
                <div className="relative w-48">
                  <HiOutlineMagnifyingGlass
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('user-management.search')}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-day-primary focus:border-transparent"
                  />
                </div>
                {canInviteUsers && (
                  <Button
                    primary
                    rounded
                    className="text-base"
                    icon={HiOutlineUser}
                    iconColor="#fff"
                    iconSize={20}
                    iconPosition={Position.RIGHT}
                    onClick={() => navigate(`${Route.INVITE_USER}`)}
                  >
                    {t('user-management.invite-new-user')}
                  </Button>
                )}
              </div>
            ) : selectedTab === ManagementTab.INVITES ? (
              canInviteUsers ? (
                <Button
                  primary
                  rounded
                  className="absolute right-0 text-base"
                  icon={HiOutlineUser}
                  iconColor="#fff"
                  iconSize={20}
                  iconPosition={Position.RIGHT}
                  onClick={() => navigate(`${Route.INVITE_USER}`)}
                >
                  {t('user-management.invite-new-user')}
                </Button>
              ) : (
                <div />
              )
            ) : (
              <div />
            )}
          </div>

          <div className="min-h-[600px] relative mt-6">
            <div
              className={clsx(
                'transition-opacity duration-200',
                selectedTab === ManagementTab.USERS
                  ? 'opacity-100 relative z-10'
                  : 'opacity-0 absolute inset-0 pointer-events-none z-0',
              )}
            >
              <UserManagementPage search={debouncedSearchTerm} />
            </div>
            <div
              className={clsx(
                'transition-opacity duration-200',
                selectedTab === ManagementTab.INVITES
                  ? 'opacity-100 relative z-10'
                  : 'opacity-0 absolute inset-0 pointer-events-none z-0',
              )}
            >
              <UserManagementInviteList />
            </div>
            <div
              className={clsx(
                'transition-opacity duration-200',
                selectedTab === ManagementTab.PROJECTS
                  ? 'opacity-100 relative z-10'
                  : 'opacity-0 absolute inset-0 pointer-events-none z-0',
              )}
            >
              <ProjectManagementPage />
            </div>
          </div>
        </div>
      </CenteredLayout>
    </FluidLayout>
  );
};
